import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Clock, 
  Home, 
  MapPin,
  TestTubes,
  FileText,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  ShoppingCart
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestDetails = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedTests, setRelatedTests] = useState([]);

  useEffect(() => {
    fetchTestDetails();
  }, [testId]);

  const fetchTestDetails = async () => {
    try {
      const response = await axios.get(`${API}/tests/${testId}`);
      setTest(response.data);
      
      // Fetch related tests in the same category
      const relatedRes = await axios.get(`${API}/tests?category=${response.data.category}`);
      setRelatedTests(relatedRes.data.filter(t => t.id !== testId).slice(0, 3));
    } catch (error) {
      console.error('Error fetching test:', error);
      toast.error('Test not found');
      navigate('/tests');
    } finally {
      setLoading(false);
    }
  };

  const handleBookTest = () => {
    if (!isAuthenticated) {
      toast.info('Please sign in to book a test');
      navigate('/login');
      return;
    }
    navigate(`/book/${testId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex justify-center items-center py-32">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (!test) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="test-details-page">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/tests')}
          className="mb-6"
          data-testid="back-to-tests-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tests
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Test Header */}
            <div className="card-clinical">
              <div className="flex items-start justify-between mb-4">
                <Badge variant="outline" className="capitalize">
                  {test.category}
                </Badge>
                <span className="mono text-sm text-slate-500">{test.code}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" data-testid="test-name">
                {test.name}
              </h1>
              <p className="text-slate-600 text-lg" data-testid="test-description">
                {test.description}
              </p>

              {/* Quick Info */}
              <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t">
                <div className="flex items-center gap-2 text-slate-600">
                  <Clock className="w-5 h-5 text-sky-600" />
                  <span>Results in {test.turnaround_hours} hours</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <TestTubes className="w-5 h-5 text-sky-600" />
                  <span>{test.sample_type} sample</span>
                </div>
                {test.home_collection_available && (
                  <div className="flex items-center gap-2 text-emerald-600">
                    <Home className="w-5 h-5" />
                    <span>Home collection available</span>
                  </div>
                )}
              </div>
            </div>

            {/* Preparation Instructions */}
            {test.preparation_instructions && (
              <div className="card-clinical">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-600" />
                  Preparation Instructions
                </h2>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <p className="text-amber-800" data-testid="test-preparation">
                      {test.preparation_instructions}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* What's Included */}
            <div className="card-clinical">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">What's Included</h2>
              <ul className="space-y-3">
                {[
                  'Professional sample collection',
                  'Laboratory analysis by certified technicians',
                  'Digital results with interpretation',
                  'Secure online access to reports',
                  'Doctor consultation support'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-600">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar - Booking Card */}
          <div className="lg:col-span-1">
            <div className="card-clinical sticky top-24">
              <div className="mb-6">
                <p className="text-sm text-slate-500 mb-1">Test Price</p>
                <p className="text-4xl font-bold text-slate-900" data-testid="test-price">
                  ₦{test.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-slate-500 mt-1">Inclusive of all taxes</p>
              </div>

              <Button 
                size="lg"
                className="w-full bg-sky-600 hover:bg-sky-700 mb-4"
                onClick={handleBookTest}
                data-testid="book-test-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Book This Test
              </Button>

              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Sample Type</span>
                  <span className="font-medium text-slate-900">{test.sample_type}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Turnaround</span>
                  <span className="font-medium text-slate-900">{test.turnaround_hours} hours</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Home Collection</span>
                  <span className={`font-medium ${test.home_collection_available ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {test.home_collection_available ? 'Available' : 'Not Available'}
                  </span>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                  MLSCN Accredited Labs
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                  100% Accurate Results
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                  Free Report Consultation
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Tests */}
        {relatedTests.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Related Tests</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {relatedTests.map((relTest) => (
                <div 
                  key={relTest.id}
                  className="card-clinical test-card cursor-pointer"
                  onClick={() => navigate(`/tests/${relTest.id}`)}
                  data-testid={`related-test-${relTest.id}`}
                >
                  <h3 className="font-semibold text-slate-900 mb-2">{relTest.name}</h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-4">{relTest.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-slate-900">₦{relTest.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <span className="text-sm text-slate-500">{relTest.turnaround_hours}h</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default TestDetails;
